import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authorizedFetch } from '../utils/api';
import browserCacheService from '../services/BrowserCacheService';
import { useCachedData } from './useCachedData';

/**
 * Daten für „Skills entdecken“ (Okt 2026, PLAN § 2.1).
 *
 * useSkillBrowse: Katalog mit Zahlen (GET /api/v1/skills/browse). Lädt erst,
 * wenn `enabled` wahr ist (Reiter „Skills“ offen): Die Startseite ist der
 * meistgeöffnete Bildschirm und soll dadurch nicht langsamer werden.
 * Stale-While-Revalidate über useCachedData. Der Schlüssel hängt am Konto,
 * nicht an der Schule: Die Antwort enthält den eigenen Bezug (`me`, `forYou`)
 * und ist um Blockierungen bereinigt, gehört also genau einer Person.
 *
 * useSkillPeople: Personen zu einem Skill und einer Rolle
 * (GET /api/v1/skills/:id/people), seitenweise über `nextCursor`.
 */

export const BROWSE_CACHE_TTL = browserCacheService.TTL.PROFILES;
export const PEOPLE_PAGE_SIZE = 30;

export const browseCacheKey = userId => (userId ? `skill_browse_${userId}` : null);

const failure = (response) => {
  const error = new Error(`Skill-Übersicht nicht verfügbar (${response.status})`);
  // useCachedData versucht 5xx (z. B. 503 BROWSE_UNAVAILABLE) selbst erneut
  error.response = { status: response.status };
  return error;
};

/** GET /api/v1/skills/browse → { categories, forYou, generatedAt } */
export const fetchSkillBrowse = async () => {
  const response = await authorizedFetch('/api/v1/skills/browse');
  if (!response.ok) throw failure(response);
  const body = await response.json();
  if (!body || body.success === false) throw failure({ status: 503 });
  return {
    categories: Array.isArray(body.categories) ? body.categories : [],
    forYou: Array.isArray(body.forYou) ? body.forYou : [],
    generatedAt: body.generatedAt || null,
  };
};

/** GET /api/v1/skills/:skillId/people → { people, nextCursor, total } */
export const fetchSkillPeople = async (skillId, role, cursor) => {
  const params = new URLSearchParams({ role, limit: String(PEOPLE_PAGE_SIZE) });
  if (cursor) params.set('cursor', cursor);
  const response = await authorizedFetch(`/api/v1/skills/${encodeURIComponent(skillId)}/people?${params}`);
  if (!response.ok) throw failure(response);
  const body = await response.json();
  return {
    people: Array.isArray(body?.people) ? body.people : [],
    nextCursor: body?.nextCursor || null,
    total: typeof body?.total === 'number' ? body.total : null,
  };
};

export function useSkillBrowse({ enabled = true } = {}) {
  const { user } = useAuth();
  const cacheKey = enabled ? browseCacheKey(user?.id) : null;
  const { data, isLoading, isRefreshing, error, retryCount, refresh } = useCachedData(
    cacheKey,
    fetchSkillBrowse,
    BROWSE_CACHE_TTL,
  );
  return { data, isLoading: Boolean(cacheKey) && isLoading, isRefreshing, error, retryCount, refresh };
}

const EMPTY = { people: [], nextCursor: null, total: null, status: 'idle', loadingMore: false };

/**
 * @param {string|null} skillId
 * @param {'offer'|'seek'} role
 * @param {{ enabled?: boolean, loader?: Function }} [options]
 *   loader(skillId, role, cursor) ersetzt den API-Abruf (Design-Katalog, Tests)
 */
export function useSkillPeople(skillId, role, { enabled = true, loader = fetchSkillPeople } = {}) {
  const [state, setState] = useState(EMPTY);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  // Antworten zu einem anderen Skill (schnell gewechselt) dürfen nichts überschreiben
  const generationRef = useRef(0);

  const loadFirst = useCallback(() => {
    if (!skillId || !enabled) return;
    const generation = ++generationRef.current;
    setState({ ...EMPTY, status: 'loading' });
    Promise.resolve()
      .then(() => loaderRef.current(skillId, role, null))
      .then((page) => {
        if (generationRef.current !== generation) return;
        setState({ people: page.people, nextCursor: page.nextCursor, total: page.total, status: 'ready', loadingMore: false });
      })
      .catch(() => {
        if (generationRef.current !== generation) return;
        setState({ ...EMPTY, status: 'error' });
      });
  }, [skillId, role, enabled]);

  useEffect(() => {
    if (!skillId || !enabled) {
      generationRef.current++;
      setState(EMPTY);
      return undefined;
    }
    loadFirst();
    // Absicht: Der Zähler soll beim Aufräumen weiterspringen (keine DOM-Ref)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { generationRef.current++; };
  }, [skillId, enabled, loadFirst]);

  const loadMore = useCallback(() => {
    if (!state.nextCursor || state.loadingMore) return;
    const generation = generationRef.current;
    const cursor = state.nextCursor;
    setState(prev => ({ ...prev, loadingMore: true, loadMoreError: false }));
    Promise.resolve()
      .then(() => loaderRef.current(skillId, role, cursor))
      .then((page) => {
        if (generationRef.current !== generation) return;
        setState((prev) => {
          const known = new Set(prev.people.map(p => p.userId));
          const fresh = page.people.filter(p => !known.has(p.userId));
          return {
            ...prev,
            people: [...prev.people, ...fresh],
            nextCursor: page.nextCursor,
            total: page.total ?? prev.total,
            loadingMore: false,
          };
        });
      })
      .catch(() => {
        if (generationRef.current !== generation) return;
        setState(prev => ({ ...prev, loadingMore: false, loadMoreError: true }));
      });
  }, [skillId, role, state.nextCursor, state.loadingMore]);

  /** Nach dem Blockieren aus der Profilkarte: Person sofort aus der Liste nehmen. */
  const removePerson = useCallback((userId) => {
    setState((prev) => {
      const people = prev.people.filter(p => p.userId !== userId);
      if (people.length === prev.people.length) return prev;
      return { ...prev, people, total: prev.total == null ? null : Math.max(0, prev.total - 1) };
    });
  }, []);

  return { ...state, loadMore, retry: loadFirst, removePerson };
}

export default useSkillBrowse;
